<script setup>
import { computed, ref, watch } from 'vue'
import { useForm, Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import SupplierPortalLayout from '@/Layouts/SupplierPortalLayout.vue'
import { makesFor, modelsFor, extractCC } from '@/composables/useVehicleCatalog.js'

defineOptions({ layout: SupplierPortalLayout })

const { t } = useI18n()

const props = defineProps({
  vehicle:         { type: Object, required: true },
  errors:          { type: Object, default: () => ({}) },
  vehicle_classes: { type: Array,  default: () => [] },
  categories:      { type: Object, default: () => ({}) },
  transmissions:   { type: Array,  default: () => [] },
  fuel_types:      { type: Array,  default: () => [] },
  rate_units:      { type: Array,  default: () => [] },
  supplier:        { type: Object, required: true },
  currency_code:   { type: String, default: 'USD' }
})

const form = useForm({
  make:                 props.vehicle.make ?? '',
  model:                props.vehicle.model ?? '',
  year:                 props.vehicle.year ?? '',
  registration:         props.vehicle.registration ?? '',
  vehicle_class:        props.vehicle.vehicle_class ?? 'two_wheeler',
  category:             props.vehicle.category ?? 'motorbike',
  transmission:         props.vehicle.transmission ?? '',
  seats:                props.vehicle.seats ?? '',
  fuel_type:            props.vehicle.fuel_type ?? '',
  colour:               props.vehicle.colour ?? '',
  daily_rate_cents:     props.vehicle.daily_rate_cents ?? 0,
  deposit_amount_cents: props.vehicle.deposit_amount_cents ?? 0,
  engine_cc:            props.vehicle.engine_cc ?? '',
  horsepower:           props.vehicle.horsepower ?? '',
  odometer_km:          props.vehicle.odometer_km ?? 0,
  last_service_km:    props.vehicle.last_service_km ?? '',
  next_service_km:      props.vehicle.next_service_km ?? '',
  notes:                props.vehicle.notes ?? '',
  photos:               []
})

// Hybrid dropdown: pick from curated catalog, or choose "Other…" to type
// a custom value. Two local refs back the UI; a watcher composes them into
// form.make / form.model so the server payload stays a plain string.
const availableMakes  = computed(() => makesFor(form.vehicle_class))
const availableModels = computed(() => modelsFor(form.vehicle_class, form.make))

const initialMakes  = makesFor(form.vehicle_class)
const initialModels = modelsFor(form.vehicle_class, form.make)

const makeChoice  = ref(form.make  && initialMakes.includes(form.make)   ? form.make  : (form.make  ? '__other' : ''))
const customMake  = ref(makeChoice.value === '__other' ? form.make : '')
const modelChoice = ref(form.model && initialModels.includes(form.model) ? form.model : (form.model ? '__other' : ''))
const customModel = ref(modelChoice.value === '__other' ? form.model : '')

watch([makeChoice, customMake], () => {
  form.make = makeChoice.value === '__other' ? customMake.value : makeChoice.value
  if (makeChoice.value !== '__other') {
    const nextModels = modelsFor(form.vehicle_class, makeChoice.value)
    if (modelChoice.value && modelChoice.value !== '__other' && !nextModels.includes(modelChoice.value)) {
      modelChoice.value = ''
      customModel.value = ''
    }
  }
})

watch([modelChoice, customModel], () => {
  form.model = modelChoice.value === '__other' ? customModel.value : modelChoice.value
})

watch(() => form.model, (newModel) => {
  if (!newModel) return
  const cc = extractCC(newModel)
  if (cc != null) form.engine_cc = cc
})

watch(() => form.vehicle_class, () => {
  makeChoice.value  = ''
  customMake.value  = ''
  modelChoice.value = ''
  customModel.value = ''
})

const subtypeOptions = computed(() => props.categories[form.vehicle_class] ?? [])

watch(() => form.vehicle_class, (newClass, oldClass) => {
  if (oldClass === undefined) return
  const allowed = props.categories[newClass] ?? []
  if (!allowed.includes(form.category)) form.category = allowed[0] ?? ''
  if (newClass !== 'four_wheeler') {
    form.transmission = ''
    form.seats        = ''
    form.fuel_type    = ''
  }
})

const isEdit = !!props.vehicle.id

// Sub-rental terms — always submitted on the supplier portal (the supplier
// IS the source). No toggle, no supplier picker. Pre-fill from the current
// open sub-rental when editing.
const sr = props.vehicle.sub_rental || {}
const subRental = ref({
  rate_amount:    sr.rate_cents != null ? (sr.rate_cents / 100).toString() : '',
  rate_unit:      sr.rate_unit || 'day',
  deposit_amount: sr.deposit_cents != null ? (sr.deposit_cents / 100).toString() : '',
  end_on:         sr.end_on || ''
})

function buildFormData() {
  const fd = new FormData()
  const fields = ['make', 'model', 'year', 'registration',
                  'vehicle_class', 'category', 'transmission', 'seats', 'fuel_type',
                  'colour', 'daily_rate_cents', 'deposit_amount_cents',
                  'engine_cc', 'horsepower',
                  'odometer_km', 'last_service_km', 'next_service_km', 'notes']
  fields.forEach(k => fd.append(`vehicle[${k}]`, form[k] ?? ''))
  form.photos.forEach(file => fd.append('vehicle[photos][]', file))

  const sr = subRental.value
  const rateCents    = Math.round(parseFloat(sr.rate_amount    || 0) * 100) || 0
  const depositCents = Math.round(parseFloat(sr.deposit_amount || 0) * 100) || 0
  fd.append('sub_rental[rate_cents]',    rateCents)
  fd.append('sub_rental[rate_unit]',     sr.rate_unit)
  fd.append('sub_rental[deposit_cents]', depositCents)
  if (sr.end_on) fd.append('sub_rental[end_on]', sr.end_on)
  return fd
}

function submit() {
  const fd = buildFormData()
  const opts = { onError: (errors) => { form.errors = errors }, preserveScroll: true }
  if (isEdit) {
    fd.append('_method', 'PATCH')
    router.post(`/vehicles/${props.vehicle.id}`, fd, opts)
  } else {
    router.post('/vehicles', fd, opts)
  }
}

function onPhotos(e) {
  form.photos = Array.from(e.target.files || [])
}

function err(field) {
  return form.errors[field] || props.errors[field]?.[0]
}

function centsToInput(cents) {
  if (cents == null) return ''
  const v = cents / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

const dailyRateDollars = {
  get: () => centsToInput(form.daily_rate_cents),
  set: (v) => { form.daily_rate_cents = Math.round(parseFloat(v) * 100) || 0 }
}
const depositDollars = {
  get: () => centsToInput(form.deposit_amount_cents),
  set: (v) => { form.deposit_amount_cents = Math.round(parseFloat(v) * 100) || 0 }
}

const inputBase = 'w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]'
const labelCls  = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
const errorCls  = 'mt-1 text-xs text-[color:var(--danger)]'
</script>

<template>
  <Link :href="isEdit ? `/vehicles/${vehicle.id}` : '/'" class="back">← {{ t('supplier_portal.vehicle_form.back') }}</Link>
  <h1 class="title">
    {{ isEdit ? t('supplier_portal.vehicle_form.title_edit') : t('supplier_portal.vehicle_form.title') }}
  </h1>
  <p class="subtitle">{{ t('supplier_portal.vehicle_form.subtitle', { supplier: supplier.name }) }}</p>

  <!-- Approval state pill — visible whenever we have one from the server -->
  <div v-if="vehicle.approval_state" class="approval-pill" :class="`approval-pill--${vehicle.approval_state}`">
    {{ t(`supplier_portal.vehicle_form.approval.${vehicle.approval_state}`) }}
    <span v-if="vehicle.rejection_reason" class="approval-pill__reason">— {{ vehicle.rejection_reason }}</span>
  </div>

  <form id="vehicle-form" @submit.prevent="submit" class="card">
    <!-- Make / model — curated dropdown + Other custom input -->
    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.make') }} *</label>
        <select
          v-model="makeChoice"
          required
          :class="[inputBase, err('make') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
        >
          <option value="">{{ t('vehicles.form.pickMake') }}</option>
          <option v-for="m in availableMakes" :key="m" :value="m">{{ m }}</option>
          <option value="__other">{{ t('vehicles.form.otherCustom') }}</option>
        </select>
        <input
          v-if="makeChoice === '__other'"
          v-model="customMake"
          type="text"
          required
          :placeholder="t('vehicles.form.typeMake')"
          :class="[inputBase, 'mt-2', 'border-[color:var(--line)]']"
        />
        <p v-if="err('make')" :class="errorCls">{{ err('make') }}</p>
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.model') }} *</label>
        <select
          v-model="modelChoice"
          required
          :disabled="!form.make"
          :class="[inputBase, err('model') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]', !form.make ? 'opacity-60' : '']"
        >
          <option value="">{{ t('vehicles.form.pickModel') }}</option>
          <option v-for="m in availableModels" :key="m" :value="m">{{ m }}</option>
          <option value="__other">{{ t('vehicles.form.otherCustom') }}</option>
        </select>
        <input
          v-if="modelChoice === '__other'"
          v-model="customModel"
          type="text"
          required
          :placeholder="t('vehicles.form.typeModel')"
          :class="[inputBase, 'mt-2', 'border-[color:var(--line)]']"
        />
        <p v-if="err('model')" :class="errorCls">{{ err('model') }}</p>
      </div>
    </div>

    <div class="grid3">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.year') }}</label>
        <input v-model="form.year" type="number" min="1900" :max="new Date().getFullYear() + 1" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.registration') }} *</label>
        <input v-model="form.registration" type="text" required :class="[inputBase, 'font-mono uppercase', err('registration') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']" />
        <p v-if="err('registration')" :class="errorCls">{{ err('registration') }}</p>
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.colour') }}</label>
        <input v-model="form.colour" type="text" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.class') }}</label>
        <select v-model="form.vehicle_class" :class="[inputBase, 'border-[color:var(--line)] capitalize']">
          <option v-for="c in vehicle_classes" :key="c" :value="c">{{ t(`vehicles.classes.${c}`) }}</option>
        </select>
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.subtype') }}</label>
        <select v-model="form.category" :class="[inputBase, 'border-[color:var(--line)] capitalize']">
          <option v-for="s in subtypeOptions" :key="s" :value="s">{{ s }}</option>
        </select>
        <p v-if="err('category')" :class="errorCls">{{ err('category') }}</p>
      </div>
    </div>

    <div v-if="form.vehicle_class === 'four_wheeler'" class="grid3">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.transmission') }} *</label>
        <select v-model="form.transmission" required :class="[inputBase, err('transmission') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]', 'capitalize']">
          <option value="" disabled>—</option>
          <option v-for="tr in transmissions" :key="tr" :value="tr">{{ tr.replace('_', ' ') }}</option>
        </select>
        <p v-if="err('transmission')" :class="errorCls">{{ err('transmission') }}</p>
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.seats') }} *</label>
        <input v-model.number="form.seats" type="number" min="1" max="30" required :class="[inputBase, err('seats') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']" />
        <p v-if="err('seats')" :class="errorCls">{{ err('seats') }}</p>
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.fuel_type') }} *</label>
        <select v-model="form.fuel_type" required :class="[inputBase, err('fuel_type') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]', 'capitalize']">
          <option value="" disabled>—</option>
          <option v-for="f in fuel_types" :key="f" :value="f">{{ f }}</option>
        </select>
        <p v-if="err('fuel_type')" :class="errorCls">{{ err('fuel_type') }}</p>
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.defaultRate') }} ({{ currency_code }})</label>
        <input :value="dailyRateDollars.get()" @input="dailyRateDollars.set($event.target.value)" type="number" min="0" step="1" :class="[inputBase, 'border-[color:var(--line)]']" />
        <p class="hint">{{ t('supplier_portal.vehicle_form.daily_rate_hint') }}</p>
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.form.subRented.deposit') }} ({{ currency_code }})</label>
        <input :value="depositDollars.get()" @input="depositDollars.set($event.target.value)" type="number" min="0" step="1" :class="[inputBase, 'border-[color:var(--line)]']" />
        <p class="hint">{{ t('supplier_portal.vehicle_form.customer_deposit_hint') }}</p>
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.engine_cc') }}</label>
        <input v-model.number="form.engine_cc" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.horsepower') }}</label>
        <input v-model.number="form.horsepower" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
    </div>

    <div class="grid3">
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.odometer_km') }}</label>
        <input v-model="form.odometer_km" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.last_service_km') }}</label>
        <input v-model="form.last_service_km" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
      <div>
        <label :class="labelCls">{{ t('vehicles.fields.next_service_km') }}</label>
        <input v-model="form.next_service_km" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
      </div>
    </div>

    <div>
      <label :class="labelCls">{{ t('vehicles.fields.photos') }}</label>
      <input
        type="file"
        accept="image/*"
        multiple
        @change="onPhotos"
        class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
      />
      <p class="hint">{{ t('supplier_portal.vehicle_form.photos_hint') }}</p>
    </div>

    <div>
      <label :class="labelCls">{{ t('vehicles.fields.notes') }}</label>
      <textarea v-model="form.notes" rows="3" :class="[inputBase, 'border-[color:var(--line)]']"></textarea>
    </div>

    <!-- Sub-rental terms — supplier is implicit, dates/rate live here. -->
    <div class="subrent">
      <h3 class="subrent__title">{{ t('supplier_portal.vehicle_form.sections.terms') }}</h3>
      <p class="hint">{{ t('supplier_portal.vehicle_form.terms_hint') }}</p>

      <div class="grid2">
        <div>
          <label :class="labelCls">{{ t('vehicles.form.subRented.rate') }} ({{ currency_code }}) *</label>
          <input v-model="subRental.rate_amount" type="number" min="0" step="1" required :class="[inputBase, 'border-[color:var(--line)]']" />
        </div>
        <div>
          <label :class="labelCls">{{ t('vehicles.form.subRented.rateUnit') }} *</label>
          <select v-model="subRental.rate_unit" required :class="[inputBase, 'border-[color:var(--line)]']">
            <option v-for="u in rate_units" :key="u" :value="u">{{ t(`vehicles.form.subRented.units.${u}`) }}</option>
          </select>
        </div>
      </div>

      <div class="grid2">
        <div>
          <label :class="labelCls">{{ t('supplier_portal.vehicle_form.fields.deposit') }} ({{ currency_code }})</label>
          <input v-model="subRental.deposit_amount" type="number" min="0" step="1" :class="[inputBase, 'border-[color:var(--line)]']" />
          <p class="hint">{{ t('vehicles.form.subRented.depositHint') }}</p>
        </div>
        <div>
          <label :class="labelCls">{{ t('supplier_portal.vehicle_form.fields.end_on') }}</label>
          <input v-model="subRental.end_on" type="date" :class="[inputBase, 'border-[color:var(--line)]']" />
          <p class="hint">{{ t('supplier_portal.vehicle_form.end_on_hint') }}</p>
        </div>
      </div>
    </div>

    <!-- Cross-field errors (e.g. sub-rental validation surfaced on :base) -->
    <div v-if="errors.base" class="errors">
      <p v-for="(m, i) in (Array.isArray(errors.base) ? errors.base : [errors.base])" :key="i">{{ m }}</p>
    </div>

    <div class="actions">
      <Link href="/" class="btn">{{ t('supplier_portal.vehicle_form.cancel') }}</Link>
      <button type="submit" class="btn btn--primary" :disabled="form.processing">
        {{ form.processing ? t('supplier_portal.vehicle_form.submitting') : t('supplier_portal.vehicle_form.submit') }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.back     { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.title    { font-family: var(--font-display, var(--display)); font-size: 1.5rem; color: var(--ink); margin: 0.5rem 0 0.25rem; }
.subtitle { color: var(--inkSoft); margin: 0 0 1rem; font-size: 0.9375rem; }
.approval-pill {
  display: inline-block; padding: 0.4rem 0.75rem; margin: 0 0 1rem;
  border-radius: 999px; font-size: 0.8125rem; font-weight: 500;
}
.approval-pill--pending  { background: color-mix(in oklch, var(--warn) 16%, var(--surface)); color: var(--warn); }
.approval-pill--approved { background: color-mix(in oklch, var(--ok) 16%, var(--surface)); color: var(--ok); }
.approval-pill--rejected { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.approval-pill__reason { font-weight: 400; }

.card {
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 0.75rem; padding: 1.25rem 1.5rem;
  display: grid; gap: 1.25rem; max-width: 48rem;
}
.grid2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
.grid3 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 640px) {
  .grid2 { grid-template-columns: 1fr 1fr; }
  .grid3 { grid-template-columns: 1fr 1fr 1fr; }
}

.subrent {
  border: 1px solid var(--line); border-radius: 0.5rem;
  padding: 1rem; display: grid; gap: 1rem;
  background: color-mix(in oklch, var(--accent) 4%, var(--surface));
}
.subrent__title { font-size: 0.875rem; font-weight: 600; color: var(--ink); margin: 0; }

.hint   { margin: 0.25rem 0 0; font-size: 0.75rem; color: var(--muted); }
.errors {
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger); padding: 0.75rem 1rem; border-radius: 0.5rem;
  font-size: 0.875rem;
}
.errors p { margin: 0; }

.actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
.btn {
  padding: 0.55rem 1rem; border: 1px solid var(--line); background: var(--surface);
  color: var(--ink); border-radius: var(--radius-button, 8px); text-decoration: none;
  font-weight: 500; cursor: pointer;
}
.btn--primary { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.btn--primary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
